import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { toast } from 'react-hot-toast';

import { useWebSocket } from './WebSocketProvider';
import { DataState } from './DataProvider';
import { useNotifications } from './NotificationProvider';

const ChangeRequestContext = createContext();

// Sample CR pre-loaded for demo — shows the full pending → approval → notification flow
const DEMO_CR = {
  id: 'CR-047',
  title: 'Add multi-currency support to invoicing module',
  raisedBy: 'Sarah Mitchell (Client)',
  date: '03 May 2026',
  classification: 'Scope addition',
  phase: 'Build — Week 6 of 12',
  assumption: 'Single currency (GBP)',
  sourceDoc: 'SOW Page 4, §2.3',
  source_origin: 'email',
  metrics: {
    timeline: '+8–11d',
    effort: '+47–63h',
    budget: '£16–22K',
    confidence: '78%',
  },
};

export const ChangeRequestProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
  const { userGoogleId: accessToken } = DataState();
  const { upsertNotification } = useNotifications();

  const [pendingCRs, setPendingCRs] = useState([DEMO_CR]);
  const pendingCRsRef = useRef([DEMO_CR]);
  const [activeCRModal, setActiveCRModal] = useState(null);
  const [stagedCRChat, setStagedCRChat] = useState(null);

  useEffect(() => {
    pendingCRsRef.current = pendingCRs;
  }, [pendingCRs]);

  const addChangeRequest = useCallback((crData) => {
    setPendingCRs((prev) => {
      if (prev.find((cr) => cr.id === crData.id)) return prev;
      toast(`New Change Request: ${crData.id}`, { icon: '📋' });
      return [crData, ...prev];
    });
  }, []);

  const approveChangeRequest = useCallback(
    (crId) => {
      const cr = pendingCRsRef.current.find((c) => c.id === crId);
      if (!cr) return;

      upsertNotification({
        id: cr.id,
        source_type: 'change_request',
        event_name: `${cr.id}: ${cr.title}`,
        timestamp: new Date().toISOString(),
        active: true,
        cr_data: cr,
      });

      toast.success(`CR ${cr.id} approved — check Notifications`);
      setPendingCRs((prev) => prev.filter((c) => c.id !== crId));
    },
    [upsertNotification],
  );

  const rejectChangeRequest = useCallback((crId) => {
    setPendingCRs((prev) => {
      const cr = prev.find((c) => c.id === crId);
      if (cr) toast(`CR ${cr.id} rejected`, { icon: '✕' });
      return prev.filter((c) => c.id !== crId);
    });
  }, []);

  const openCRModal = useCallback((crData) => {
    setActiveCRModal(crData);
  }, []);

  const closeCRModal = useCallback(() => {
    setActiveCRModal(null);
  }, []);

  const stageCRChat = useCallback((payload) => {
    setStagedCRChat(payload);
  }, []);

  const clearStagedCRChat = useCallback(() => {
    setStagedCRChat(null);
  }, []);

  // Listen for backend-emitted change_request_detected events
  useEffect(() => {
    if (!isConnected || !accessToken) return;
    const unsubscribe = subscribe({
      change_request_detected: (data) => addChangeRequest(data),
    });
    return () => unsubscribe();
  }, [isConnected, accessToken, subscribe, addChangeRequest]);

  return (
    <ChangeRequestContext.Provider
      value={{
        pendingCRs,
        addChangeRequest,
        approveChangeRequest,
        rejectChangeRequest,
        activeCRModal,
        openCRModal,
        closeCRModal,
        stagedCRChat,
        stageCRChat,
        clearStagedCRChat,
      }}
    >
      {children}
    </ChangeRequestContext.Provider>
  );
};

export const useChangeRequests = () => useContext(ChangeRequestContext);
